import React, { useState, useEffect, useRef } from 'react';
import { TiArrowUnsorted } from "react-icons/ti";
import { TbPencil } from "react-icons/tb";
import { FaEdit, FaTrash } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useCurrency } from '../../../../../../../context/currencyContext';
import './Pricing.css';

// react-select shared styles
const selectStyles = {
  control: (provided) => ({
    ...provided,
    padding: '2px 0',
    border: '1px solid #ccc',
    borderRadius: '3px',
    fontSize: '14px',
    minHeight: '34px',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

// Custom Dropdown Component
const CustomDropdown = ({ label, name, value, options, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (selectedValue) => {
    onChange({ target: { name, value: selectedValue } });
    setIsOpen(false);
  };

  return (
    <div className="pricing-dropdown-container" ref={dropdownRef}>
      <div
        className={`pricing-dropdown-header ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span>{value || `Select ${label.toLowerCase()}`}</span>
        <span className={`pricing-dropdown-arrow ${isOpen ? 'open' : ''}`}>
          <IoIosArrowDown />
        </span>
      </div>
      {isOpen && !disabled && (
        <div className="pricing-dropdown-list">
          {options.map((option) => (
            <div
              key={option}
              className="pricing-dropdown-item"
              onClick={() => handleChange(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper components
const SimpleNumberInput = ({ label, name, value, onChange, min, step = "any" }) => (
  <div className="pricing-form-row">
    <label className="pricing-form-label">{label}</label>
    <input
      type="number"
      name={name}
      value={value}
      onChange={onChange}
      className="pricing-form-number-input"
      min={min}
      step={step}
    />
  </div>
);

const SimpleTextInput = ({ label, name, value, onChange }) => (
  <div className="pricing-form-row">
    <label className="pricing-form-label">{label}</label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      className="pricing-form-input"
    />
  </div>
);

const SimpleFormField = ({ label, name, children, value, onChange }) => (
  <div className="pricing-form-row">
    <label className="pricing-form-label">{label}</label>
    <div className="pricing-form-field">
      {React.cloneElement(children, { name, onChange, value, className: 'pricing-form-input' })}
    </div>
  </div>
);

// Add/Edit Price Form
const AddPriceForm = ({ closeModal, onAddPrice, onEditPrice, editData, isEditing, editIndex }) => {
  const { formatAmount } = useCurrency();

  const [formData, setFormData] = useState(() => {
    if (isEditing && editData) {
      const pricingFor = editData.pricingFor || 'Group';
      return {
        price: editData.price || 0,
        pricingFor,
        group: pricingFor === 'Group' ? editData.target : 'default',
        newGroup: '',
        accountId: pricingFor === 'Single Account' ? editData.target : '',
        accountName: pricingFor === 'Single Account' ? editData.accountName : '',
        providerId: pricingFor === 'Provider' ? editData.target : '',
        providerName: pricingFor === 'Provider' ? editData.providerName : '',
        extCost: editData.externalData?.extCost || 0,
        extPID: editData.externalData?.extPID || '',
        extVPID: editData.externalData?.extVPID || '',
        extDesc: editData.externalData?.extDesc || '',
      };
    }
    return {
      price: 0,
      pricingFor: 'Group',
      group: 'default',
      newGroup: '',
      accountId: '',
      accountName: '',
      providerId: '',
      providerName: '',
      extCost: 0,
      extPID: '',
      extVPID: '',
      extDesc: '',
    };
  });

  const [accounts, setAccounts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [error, setError] = useState(null);
  const procedureId = sessionStorage.getItem('procedureId');

  // Get userLabId from localStorage
  const getUserLabId = () => {
    let labId = localStorage.getItem('userLabId');
    if (!labId) {
      labId = localStorage.getItem('labId') ||
               localStorage.getItem('labID') ||
               localStorage.getItem('userId') ||
               localStorage.getItem('userID');
    }
    return labId;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const userLabId = getUserLabId();

      if (!userLabId) {
        setError("User Lab ID not found in localStorage. Please login again.");
        setLoading(false);
        return;
      }

      try {
        // Fetch accounts
        const accountsResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`);
        setAccounts(accountsResponse.data.data || []);

        // Fetch groups
        const groupsResponse = await axios.get('https://hms.backend.hiragroupcloud.com/api/prices/get', {
          params: { userLabId: userLabId }
        });
        if (groupsResponse.data) {
          setGroups(groupsResponse.data.map(g => g.name) || []);
        } else {
          throw new Error('Invalid response format for groups');
        }

      } catch (err) {
        setError('Failed to load accounts or groups.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch providers when account changes
  useEffect(() => {
    const fetchProviders = async () => {
      if (formData.pricingFor === 'Provider' && formData.accountId) {
        setLoadingProviders(true);
        try {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/contacts/${formData.accountId}`);

          let providersData = [];
          if (response.data?.data && Array.isArray(response.data.data)) {
            providersData = response.data.data;
          } else if (response.data?.contacts && Array.isArray(response.data.contacts)) {
            providersData = response.data.contacts;
          } else if (Array.isArray(response.data)) {
            providersData = response.data;
          } else if (response.data?.providers && Array.isArray(response.data.providers)) {
            providersData = response.data.providers;
          } else {
            providersData = [];
          }

          setProviders(providersData);
          setFilteredProviders(providersData);

          // Clear selected provider if it doesn't belong to the selected account
          if (formData.providerId && !providersData.find(p => {
            const providerId = p._id || p.id || p.providerId || p.contactId;
            return providerId === formData.providerId;
          })) {
            setFormData(prev => ({ ...prev, providerId: '', providerName: '' }));
          }
        } catch (err) {
          setProviders([]);
          setFilteredProviders([]);
        } finally {
          setLoadingProviders(false);
        }
      } else {
        setProviders([]);
        setFilteredProviders([]);
      }
    };

    fetchProviders();
  }, [formData.accountId, formData.pricingFor]);

  useEffect(() => {
    if (!loading && isEditing && formData.pricingFor === 'Group' && formData.group && formData.group !== 'default' && !groups.includes(formData.group)) {
      setFormData(prev => ({ ...prev, group: 'New Group', newGroup: prev.group }));
    }
  }, [loading, groups, isEditing, formData.pricingFor, formData.group]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountChange = (selectedOption) => {
    const accountId = selectedOption ? selectedOption.value : '';
    const accountName = selectedOption ? selectedOption.label : '';
    setFormData((prev) => ({
      ...prev,
      accountId,
      accountName,
      providerId: '',
      providerName: ''
    }));
  };

  const handleProviderChange = (selectedOption) => {
    const providerId = selectedOption ? selectedOption.value : '';
    const providerName = selectedOption ? selectedOption.label : '';
    setFormData((prev) => ({ ...prev, providerId, providerName }));
  };

  // Function to extract provider name from provider object
  const getProviderName = (provider) => {
    if (!provider) return 'Unnamed Provider';

    return provider.providerName ||
           provider.name ||
           provider.fullName ||
           provider.contactName ||
           provider.provider_name ||
           provider.contact_name ||
           provider.displayName ||
           provider.title ||
           (provider.firstName && provider.lastName ? `${provider.firstName} ${provider.lastName}` : null) ||
           (provider.first_name && provider.last_name ? `${provider.first_name} ${provider.last_name}` : null) ||
           provider.email ||
           provider.phone ||
           'Unnamed Provider';
  };

  // Function to extract provider ID from provider object
  const getProviderId = (provider) => {
    return provider._id || provider.id || provider.providerId || provider.contactId;
  };

  // Map providers for Select component
  const providerOptions = filteredProviders.map(provider => ({
    value: getProviderId(provider),
    label: getProviderName(provider)
  })).filter(option => option.value);

  // Submit form - Add or Edit price
  const handleSubmit = async () => {
    const userLabId = getUserLabId();
    if (!userLabId) {
      alert('User Lab ID not found in localStorage. Please login again.');
      return;
    }

    if (!procedureId) {
      alert('Procedure ID is missing from sessionStorage. Please select a procedure first.');
      return;
    }

    if (formData.price === '' || formData.price === null || formData.price === undefined) {
      alert('Price is required.');
      return;
    }

    if (!formData.pricingFor) {
      alert('Pricing For is required.');
      return;
    }

    let effectiveTarget = '';
    let effectiveAccountName = '';
    let effectiveProviderName = '';

    if (formData.pricingFor === 'Group') {
      effectiveTarget = formData.group === 'New Group' ? formData.newGroup : formData.group;
      if (!effectiveTarget) {
        alert('Please select or create a group.');
        return;
      }

      if (formData.group === 'New Group') {
        const groupName = formData.newGroup;
        if (groupName) {
          try {
            const groupPayload = {
              name: groupName,
              userLabId: userLabId
            };
            await axios.post('https://hms.backend.hiragroupcloud.com/api/prices/add', groupPayload);
            const groupsResponse = await axios.get('https://hms.backend.hiragroupcloud.com/api/prices/get', {
              params: { userLabId: userLabId }
            });
            setGroups(groupsResponse.data.map(g => g.name) || []);
            setFormData(prev => ({ ...prev, group: groupName }));
          } catch (err) {
            if (err.response && err.response.status === 400 && err.response.data.message === 'Group already exists') {
              // Continue if group already exists
            } else {
              alert('Failed to create new group: ' + (err.response?.data?.message || err.message));
              return;
            }
          }
        } else {
          alert('New group name is required.');
          return;
        }
      }
    } else if (formData.pricingFor === 'Single Account') {
      effectiveTarget = formData.accountId;
      effectiveAccountName = formData.accountName;
      if (!effectiveTarget) {
        alert('Account selection is required for Single Account pricing.');
        return;
      }
    } else if (formData.pricingFor === 'Provider') {
      effectiveTarget = formData.providerId;
      effectiveProviderName = formData.providerName;
      if (!effectiveTarget) {
        alert('Provider selection is required for Provider pricing.');
        return;
      }
      if (!formData.accountId) {
        alert('Please select an account first.');
        return;
      }
    }

    const pricePayload = {
      procedureId,
      price: parseFloat(formData.price) || 0,
      pricingFor: formData.pricingFor,
      target: formData.pricingFor === 'Default' ? undefined : effectiveTarget,
      ...(formData.pricingFor === 'Single Account' && { accountName: effectiveAccountName }),
      ...(formData.pricingFor === 'Provider' && { providerName: effectiveProviderName }),
      externalData: {
        extCost: parseFloat(formData.extCost) || 0,
        extPID: formData.extPID || '',
        extVPID: formData.extVPID || '',
        extDesc: formData.extDesc || '',
      },
      userLabId: userLabId,
    };

    try {
      if (isEditing) {
        const id = editData._id;
        const response = await axios.put(`https://hms.backend.hiragroupcloud.com/api/prices/edit/${id}`, pricePayload);
        onEditPrice(response.data, editIndex);
      } else {
        const response = await axios.post('https://hms.backend.hiragroupcloud.com/api/prices', pricePayload);
        onAddPrice(response.data);
      }
      closeModal();
    } catch (error) {
      alert('Failed to save price: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredGroups = groups.filter(g => g !== 'default');
  const groupOptions = ['default', 'New Group', ...filteredGroups];

  const accountSelectValue = accounts.find(a => a.accountId === formData.accountId)
    ? { value: formData.accountId, label: formData.accountName }
    : null;
  const accountOptions = accounts.map(a => ({ value: a.accountId, label: a.account_name }));

  return (
    <div className="pricing-dialog">
      <div className="pricing-dialog-header">
        <span>{isEditing ? 'Edit Price' : 'Add Price'}</span>
        <span className="pricing-dialog-close" onClick={closeModal}>&times;</span>
      </div>
      <div className="pricing-dialog-content">
        {error && <p style={{ color: 'red' }}>{error}</p>}
        {loading && <p>Loading...</p>}
        <div className="pricing-section-header-in-form">General</div>
        <SimpleNumberInput
          label={`Price (${formatAmount(0).replace('0.00', '')})`}
          name="price"
          value={formData.price}
          onChange={handleInputChange}
          min="0"
          step="0.01"
        />
        <SimpleFormField label="Pricing For" name="pricingFor" value={formData.pricingFor} onChange={handleInputChange}>
          <select>
            <option value="Group">Group</option>
            <option value="Single Account">Single Account</option>
            <option value="Provider">Provider</option>
            <option value="Default">Default</option>
          </select>
        </SimpleFormField>

        {formData.pricingFor === 'Group' ? (
          <>
            <div className="pricing-form-row">
              <label className="pricing-form-label">Group</label>
              <div className="pricing-form-field">
                <CustomDropdown
                  label="Group"
                  name="group"
                  value={formData.group}
                  options={groupOptions}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>
            </div>
            {formData.group === 'New Group' && (
              <SimpleTextInput label="New Group" name="newGroup" value={formData.newGroup} onChange={handleInputChange} />
            )}
          </>
        ) : formData.pricingFor === 'Single Account' ? (
          <div className="pricing-form-row">
            <label className="pricing-form-label">Account</label>
            <div className="pricing-form-field">
              <Select
                options={accountOptions}
                value={accountSelectValue}
                onChange={handleAccountChange}
                isDisabled={loading}
                placeholder="Select Account"
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        ) : formData.pricingFor === 'Provider' ? (
          <>
            <div className="pricing-form-row">
              <label className="pricing-form-label">Account</label>
              <div className="pricing-form-field">
                <Select
                  options={accountOptions}
                  value={accountSelectValue}
                  onChange={handleAccountChange}
                  isDisabled={loading}
                  placeholder="Select Account"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
            <div className="pricing-form-row">
              <label className="pricing-form-label">Provider</label>
              <div className="pricing-form-field">
                <Select
                  options={providerOptions}
                  value={providerOptions.find(p => p.value === formData.providerId)}
                  onChange={handleProviderChange}
                  isDisabled={loading || loadingProviders || !formData.accountId}
                  placeholder={!formData.accountId ? "Select Account first" : loadingProviders ? "Loading providers..." : "Select Provider"}
                  isLoading={loadingProviders}
                  noOptionsMessage={() => !formData.accountId ? "Please select an account first" : "No providers found"}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </>
        ) : null}

        <div className="pricing-section-header-in-form">Pricing Attributes</div>
        <div className="pricing-description-text">Choose procedure attributes that this price is dependent on, if any.</div>

        <div className="pricing-section-header-in-form">External data</div>
        <SimpleNumberInput label={`Ext. Cost (${formatAmount(0).replace('0.00', '')})`} name="extCost" value={formData.extCost} onChange={handleInputChange} min="0" step="0.01" />
        <SimpleTextInput label="Ext. PID" name="extPID" value={formData.extPID} onChange={handleInputChange} />
        <SimpleTextInput label="Ext. VPID" name="extVPID" value={formData.extVPID} onChange={handleInputChange} />
        <SimpleTextInput label="Ext. Desc." name="extDesc" value={formData.extDesc} onChange={handleInputChange} />
      </div>
      <div className="pricing-dialog-footer">
        <button className="pricing-cancel-btn" onClick={closeModal}>Cancel</button>
        <button className="pricing-add-btn-modal" onClick={handleSubmit}>{isEditing ? 'Update' : 'Add'}</button>
      </div>
    </div>
  );
};

// Main Pricing Component
const Pricing = () => {
  const { formatAmount } = useCurrency();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [accountsMap, setAccountsMap] = useState(new Map());
  const [providersMap, setProvidersMap] = useState(new Map());
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const procedureId = sessionStorage.getItem('procedureId');

  const getUserLabId = () => {
    let labId = localStorage.getItem('userLabId');
    if (!labId) {
      labId = localStorage.getItem('labId') ||
               localStorage.getItem('labID') ||
               localStorage.getItem('userId') ||
               localStorage.getItem('userID');
    }
    return labId;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const userLabId = getUserLabId();

      if (!userLabId) {
        setError("User Lab ID not found in localStorage. Please login again.");
        setLoading(false);
        return;
      }

      try {
        if (!procedureId) {
          setError('No Procedure ID found in sessionStorage. Please select a procedure to edit.');
          return;
        }

        const pricesResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/prices/procedure/${procedureId}`);
        setTableData(pricesResponse.data || []);

        const accountsResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`);
        const accList = accountsResponse.data.data || [];
        const accMap = new Map(accList.map(a => [a.accountId, a.account_name]));
        setAccountsMap(accMap);

        setProvidersMap(new Map());

      } catch (err) {
        setError('Failed to load prices or accounts: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (procedureId) fetchData();
  }, [procedureId]);

  const openModal = () => {
    if (!procedureId) {
      alert('No Procedure ID found. Please select a procedure first.');
      return;
    }
    if (!getUserLabId()) {
      alert('User Lab ID not found in localStorage. Please login again.');
      return;
    }
    setIsEditing(false);
    setEditData(null);
    setEditIndex(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index) => {
    setIsEditing(true);
    setEditIndex(index);
    setEditData(tableData[index]);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleAddPrice = (newPrice) => {
    setTableData((prev) => [...prev, newPrice]);
  };

  const handleEditPrice = (updatedPrice, index) => {
    setTableData((prev) => prev.map((row, i) => (i === index ? updatedPrice : row)));
  };

  const handleDeletePrice = async (index) => {
    const userLabId = getUserLabId();
    const id = tableData[index]._id;
    try {
      await axios.delete(`https://hms.backend.hiragroupcloud.com/api/prices/${id}`, {
        data: {
          procedureId,
          userLabId: userLabId
        }
      });
      setTableData((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      alert('Failed to delete price');
    }
  };

  // SweetAlert on Save button click
  const handleSave = async () => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: 'Pricing data saved successfully!',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') return formatAmount(0);
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    return formatAmount(isNaN(numericPrice) ? 0 : numericPrice);
  };

  const formatExtCost = (extCost) => {
    if (extCost === null || extCost === undefined || extCost === '') return formatAmount(0);
    const numericExtCost = typeof extCost === 'string' ? parseFloat(extCost) : extCost;
    return formatAmount(isNaN(numericExtCost) ? 0 : numericExtCost);
  };

  const getDisplayValue = (row) => {
    if (row.pricingFor === 'Default') return 'Default';
    if (row.pricingFor === 'Group') return row.target;
    if (row.pricingFor === 'Single Account') {
      return row.accountName || accountsMap.get(row.target) || row.target;
    }
    if (row.pricingFor === 'Provider') {
      return row.providerName || row.target;
    }
    return row.target;
  };

  return (
    <div className="pricing-container">
      {error && <p className="pricing-error">{error}</p>}
      {loading && <p className="pricing-loading">Loading...</p>}

      <div className="pricing-table-wrapper">
        <table className="pricing-table">
          <thead>
            <tr>
              <th>Price ({formatAmount(0).replace('0.00', '')}) <TiArrowUnsorted className="pricing-sort-icon" /></th>
              <th>Group/Account/Provider <TiArrowUnsorted className="pricing-sort-icon" /></th>
              <th>Pricing For <TiArrowUnsorted className="pricing-sort-icon" /></th>
              <th>Attributes <TiArrowUnsorted className="pricing-sort-icon" /></th>
              <th>Ext. Cost ({formatAmount(0).replace('0.00', '')}) <TiArrowUnsorted className="pricing-sort-icon" /></th>
              <th>External Data <TiArrowUnsorted className="pricing-sort-icon" /></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={row._id || index}>
                <td data-label="Price">{formatPrice(row.price)}</td>
                <td data-label="Group/Account/Provider">{getDisplayValue(row)}</td>
                <td data-label="Pricing For">{row.pricingFor}</td>
                <td data-label="Attributes">{row.attributes || 'None'}</td>
                <td data-label="Ext. Cost">{formatExtCost(row.externalData?.extCost)}</td>
                <td data-label="External Data">{`${row.externalData?.extPID || ''}, ${row.externalData?.extVPID || ''}, ${row.externalData?.extDesc || ''}`}</td>
                <td data-label="Actions">
                  <FaEdit className="pricing-action-icon" onClick={() => openEditModal(index)} title="Edit" />
                  <FaTrash className="pricing-action-icon" onClick={() => handleDeletePrice(index)} title="Delete" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="pricing-add-btn" onClick={openModal}>+</button>
      <div className="pricing-note">* You will need to click the save button below to actually save the pricing.</div>

      <div className="pricing-section-header">Invoice Details</div>
      <div className="pricing-section"><label><input type="checkbox" /> Roll-up Attribute Prices into Procedure on Invoice</label></div>

      <div className="pricing-section-header">Tax Details</div>
      <div className="pricing-section">Taxable?</div>

      <div className="pricing-section-header">Revenue Centers Details</div>
      <div className="pricing-section">Revenue Centers<span style={{ color: '#333' }}>Select Revenue Centers</span></div>

      <div className="pricing-attribute-header">Attribute Prices</div>
      <div className="pricing-attribute-section"><div className="pricing-attribute-text">No attribute prices</div></div>

      <button className="pricing-save-btn" onClick={handleSave}><TbPencil className="pricing-save-icon" /> Save</button>

      {isModalOpen && (
        <div className="pricing-modal-overlay">
          <AddPriceForm
            closeModal={closeModal}
            onAddPrice={handleAddPrice}
            onEditPrice={handleEditPrice}
            editData={editData}
            isEditing={isEditing}
            editIndex={editIndex}
          />
        </div>
      )}
    </div>
  );
};

export default Pricing;